import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { jobsAPI, applicationsAPI } from '../../services/api';
import { formatSalary, formatDate, timeAgo } from '../../utils/helpers';
import { FiMapPin, FiClock, FiBriefcase, FiDollarSign, FiUsers, FiArrowLeft, FiSend, FiEdit, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import './Jobs.css';

const JobDetail = () => {
  const { id } = useParams();
  const { isAuthenticated, isRecruiter, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [showApplyForm, setShowApplyForm] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await jobsAPI.get(id);
        setJob(data.data || data.job || data);
        // Check if already applied
        if (data.data?.has_applied || data.has_applied) setApplied(true);
      } catch (err) {
        toast.error('Job not found');
        navigate('/jobs');
      }
      setLoading(false);
    };
    load();
  }, [id, navigate]);

  const handleApply = async () => {
    if (!isAuthenticated) { navigate('/login', { state: { from: { pathname: `/jobs/${id}` } } }); return; }
    setApplying(true);
    try {
      await applicationsAPI.apply({ job_id: parseInt(id), cover_letter: coverLetter });
      setApplied(true);
      setShowApplyForm(false);
      toast.success('Application submitted!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to apply';
      if (msg.toLowerCase().includes('already')) setApplied(true);
      toast.error(msg);
    }
    setApplying(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!job) return null;

  const isOwner = user && (job.recruiter_id === user.id || job.user_id === user.id || job.company_id === user.company_id);

  return (
    <div className="job-detail-page">
      <div className="container">
        <Link to="/jobs" className="back-link"><FiArrowLeft /> Back to Jobs</Link>

        <div className="job-detail-grid">
          <div className="job-detail-main">
            <div className="card animate-fade-in">
              <div className="job-detail-header">
                <div className="job-company-logo lg">{(job.company_name || 'C')[0]}</div>
                <div>
                  <h1>{job.title}</h1>
                  <div className="job-detail-meta">
                    <span>{job.company_name || 'Company'}</span>
                    <span><FiMapPin /> {job.location_name || job.location || 'South Africa'}</span>
                    <span><FiClock /> {timeAgo(job.created_at) || 'Recently posted'}</span>
                  </div>
                </div>
              </div>

              <div className="job-detail-tags">
                <span className="badge">{job.job_type || 'Full-time'}</span>
                {job.experience_level && <span className="badge badge-purple">{job.experience_level}</span>}
                {job.is_remote && <span className="badge badge-success">Remote</span>}
                {job.category_name && <span className="badge">{job.category_name}</span>}
              </div>

              <div className="job-detail-section">
                <h3>Description</h3>
                <div className="job-description" dangerouslySetInnerHTML={{ __html: (job.description || '').replace(/\n/g, '<br/>') }} />
              </div>

              {job.requirements && (
                <div className="job-detail-section">
                  <h3>Requirements</h3>
                  <div className="job-description" dangerouslySetInnerHTML={{ __html: job.requirements.replace(/\n/g, '<br/>') }} />
                </div>
              )}

              {job.benefits && (
                <div className="job-detail-section">
                  <h3>Benefits</h3>
                  <div className="job-description" dangerouslySetInnerHTML={{ __html: job.benefits.replace(/\n/g, '<br/>') }} />
                </div>
              )}

              {(job.skills || job.required_skills) && (
                <div className="job-detail-section">
                  <h3>Skills Required</h3>
                  <div className="skills-list">
                    {(job.skills || job.required_skills || []).map((s, i) => (
                      <span key={i} className="skill-tag">{typeof s === 'string' ? s : s.name}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="job-detail-sidebar">
            <div className="card">
              <h4 style={{ marginBottom: '1rem' }}>Job Overview</h4>
              <div className="sidebar-info">
                <div className="sidebar-row"><FiDollarSign /> <div><label>Salary</label><span>{formatSalary(job.salary_min, job.salary_max)}</span></div></div>
                <div className="sidebar-row"><FiBriefcase /> <div><label>Type</label><span>{job.job_type || 'Full-time'}</span></div></div>
                <div className="sidebar-row"><FiMapPin /> <div><label>Location</label><span>{job.location_name || job.location || 'South Africa'}</span></div></div>
                {job.experience_level && <div className="sidebar-row"><FiUsers /> <div><label>Experience</label><span>{job.experience_level}</span></div></div>}
                <div className="sidebar-row"><FiClock /> <div><label>Posted</label><span>{formatDate(job.created_at)}</span></div></div>
                {job.deadline && <div className="sidebar-row"><FiClock /> <div><label>Deadline</label><span>{formatDate(job.deadline)}</span></div></div>}
              </div>

              {/* Actions */}
              {isOwner || isAdmin ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.25rem' }}>
                  <Link to={`/jobs/${id}/edit`} className="btn btn-primary btn-block"><FiEdit /> Edit Job</Link>
                  <button className="btn btn-danger btn-block" onClick={handleDelete}><FiTrash2 /> Delete</button>
                </div>
              ) : (
                <>
                  {applied ? (
                    <div className="applied-badge">✓ Application Submitted</div>
                  ) : showApplyForm ? (
                    <div style={{ marginTop: '1.25rem' }}>
                      <div className="form-group">
                        <label>Cover Letter (optional)</label>
                        <textarea rows="4" placeholder="Why are you a great fit for this role?" value={coverLetter} onChange={(e) => setCoverLetter(e.target.value)} />
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button className="btn btn-primary btn-block" onClick={handleApply} disabled={applying}>
                          {applying ? 'Submitting...' : 'Submit Application'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => setShowApplyForm(false)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button className="btn btn-primary btn-block btn-lg" onClick={() => isAuthenticated ? setShowApplyForm(true) : navigate('/login')} style={{ marginTop: '1.25rem' }}>
                      <FiSend /> Apply Now
                    </button>
                  )}
                </>
              )}
            </div>

            {job.company_name && (
              <div className="card" style={{ marginTop: '1rem' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>About {job.company_name}</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.6 }}>
                  {job.company_description || `${job.company_name} is a company based in South Africa.`}
                </p>
                {job.company_id && <Link to={`/companies/${job.company_id}`} style={{ fontSize: '0.85rem', marginTop: '0.75rem', display: 'inline-block' }}>View Company Profile →</Link>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetail;
